import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Meeting } from '../../types';
import { formatDatePath } from '../../utils';
import { formatTipsPageUrl } from '../../utils/urlUtils.ts';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalysisType } from '../../contexts/AnalysisTypeContext';
import { useGlobalPaywall } from '../../hooks/useGlobalPaywall';
import analyticsService from '../../services/analytics';
import { getTrackConditionStyle, formatTrackCondition, shouldShowTrackCondition } from '../../utils/trackConditionUtils';
import { Tooltip } from '../ui/Tooltip';
import { useUpgradeTooltip } from '../../hooks/useUpgradeTooltip';
import './MeetingCard.css';

// Helper function to convert day of week number to day name
const getDayName = (dayOfWeek: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || '';
};

interface MeetingCardProps {
  meeting: Meeting;
  className?: string;
  isPreview?: boolean;
  accessibleTracks?: string[];
}

export const MeetingCard: React.FC<MeetingCardProps> = ({ meeting, className = '', isPreview = false, accessibleTracks = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [isIconsExpanded, setIsIconsExpanded] = useState(false);
  const { isAuthenticated } = useAuth();
  const { isPaywallTestUser } = useAnalysisType();
  const { shouldEnforcePaywall, shouldEnforcePaywallForDate } = useGlobalPaywall();
  const { getUpgradeMessage } = useUpgradeTooltip();
  
  const toggleExpanded = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newExpandedState = !expanded;
    setExpanded(newExpandedState);
    
    // Track analytics for expand/collapse
    try {
      const meetingDate = new Date(meeting.meeting_date);
      const formattedDate = meetingDate.toISOString().split('T')[0];
      
      analyticsService.trackEvent('meeting_card_expanded', {
        pageType: 'home',
        trackName: meeting.track.name,
        selectedDate: formattedDate,
        metadata: {
          expanded: newExpandedState,
          trackState: meeting.track.state,
          trackType: meeting.track.type,
          hasPerformanceData: !!(meeting.performanceImproved),
          totalRaces: meeting.performanceImproved?.total_races || 0,
          topPickROI: meeting.performanceImproved?.top_1_roi || 0
        }
      });
      
      console.log('📊 Analytics: Meeting card', newExpandedState ? 'expanded' : 'collapsed', 'for', meeting.track.name);
    } catch (error) {
      console.error('Analytics error:', error);
      // Don't let analytics errors break the app
    }
  };
  
  // Get performance metrics from the performanceImproved data
  const getPerformanceMetrics = () => {
    if (!meeting.performanceImproved) {
      return {
        totalRaces: 0,
        totalWinners: 0,
        top1Winners: 0,
        top1Picks: 0,
        totalWinnersPercentage: 0,
        top1WinnersPercentage: 0,
        roi: 0
      };
    }
    
    const perf = meeting.performanceImproved;
    
    // Calculate total winners (sum of all pick types)
    const totalWinners = 
      perf.top_1_winners + 
      perf.top_2_winners + 
      perf.top_3_winners + 
      perf.value_winners;
    
    // Calculate total picks (excluding scratched)
    const totalPicks = 
      (perf.top_1_picks - perf.top_1_scratched) +
      (perf.top_2_picks - perf.top_2_scratched) +
      (perf.top_3_picks - perf.top_3_scratched) +
      (perf.value_picks - perf.value_scratched);
    
    // Calculate top 1 picks (excluding scratched)
    const top1Picks = perf.top_1_picks - perf.top_1_scratched;
    
    // Calculate percentages
    const totalWinnersPercentage = perf.total_races > 0 ? (totalWinners / perf.total_races) * 100 : 0;
    const top1WinnersPercentage = top1Picks > 0 ? (perf.top_1_winners / top1Picks) * 100 : 0;
    
    // Use backend ROI value - no fallback calculation as it would be inaccurate
    // Real ROI calculation requires actual starting prices from race results
    let roi = perf.top_1_roi;
    
    return {
      totalRaces: perf.total_races,
      totalWinners,
      top1Winners: perf.top_1_winners,
      top1Picks,
      totalWinnersPercentage,
      top1WinnersPercentage,
      roi: roi // Keep null if it's null, don't convert to 0
    };
  };
  
  // Get performance metrics
  const meetingStats = getPerformanceMetrics();
  
  // Get the day of week from the meeting date (0 = Sunday, 6 = Saturday)
  const meetingDate = new Date(meeting.meeting_date);
  const dayOfWeek = meetingDate.getDay();
  
  // Check if we have performance data to display
  const hasPerformanceData = meeting.performanceImproved !== undefined && meeting.performanceImproved !== null;
  
  // Get percentage-based background style with diagonal lines
  const getPercentageBackgroundStyle = () => {
    if (!hasPerformanceData || !meeting.performanceImproved) {
      return {}; // No special background for meetings without performance data
    }
    
    // Calculate total winners across all pick types
    const perf = meeting.performanceImproved;
    const totalWinners = 
      perf.top_1_winners + 
      perf.top_2_winners + 
      perf.top_3_winners + 
      perf.value_winners;
    
    const totalRaces = perf.total_races;
    
    // Calculate winner percentage
    const winnerPercentage = totalRaces > 0 ? (totalWinners / totalRaces) * 100 : 0;
    const greenPercentage = Math.min(100, Math.max(0, winnerPercentage));
    
    // Create the background with percentage-based diagonal patterns
    // For diagonal gradients, we need to adjust the percentage to account for the 45-degree angle
    // The diagonal distance is longer than horizontal, so we need to scale appropriately
    const diagonalOffset = greenPercentage; // Use actual percentage for accurate representation
    
    return {
      backgroundImage: `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.4) 10px,
          rgba(255, 255, 255, 0.4) 20px
        ),
        linear-gradient(45deg, 
          transparent 0%, 
          transparent ${diagonalOffset}%, 
          rgba(244, 67, 54, 0.15) ${diagonalOffset}%, 
          rgba(244, 67, 54, 0.15) 100%
        ),
        linear-gradient(45deg, 
          rgba(39, 174, 96, 0.15) 0%, 
          rgba(39, 174, 96, 0.15) ${diagonalOffset}%, 
          transparent ${diagonalOffset}%, 
          transparent 100%
        )
      `
    };
  };
  
  // Use the actual day of week from the meeting date for the day name
  const dayName = getDayName(dayOfWeek);
  
  // Check if the meeting is abandoned
  const isAbandoned = meeting.trackConditions?.abandoned === true;
  
  // Check if user has access to this track
  const checkTrackAccess = (): boolean => {
    if (!isAuthenticated || !accessibleTracks) {
      console.log('🔍 [MEETING CARD DEBUG] No auth or accessibleTracks:', { isAuthenticated, accessibleTracks });
      return false;
    }
    
    // Private meetings are controlled by user-specific feature flags
    // If they appear in the results, the user already has access
    if (meeting.is_private) {
      console.log('🔍 [MEETING CARD DEBUG] Private meeting, granting access');
      return true;
    }
    
    const hasAccess = accessibleTracks.includes(meeting.track.track_id);
    console.log('🔍 [MEETING CARD DEBUG] Track access check:', {
      trackId: meeting.track.track_id,
      trackName: meeting.track.name,
      accessibleTracks,
      hasAccess
    });
    return hasAccess;
  };

  const hasTrackAccess = checkTrackAccess();
  
  // Use date-aware paywall logic for upgrade styling
  const meetingDateForPaywall = new Date(meeting.meeting_date);
  const shouldEnforceForThisDate = shouldEnforcePaywallForDate(meetingDateForPaywall);
  const needsUpgrade = isAuthenticated && !hasTrackAccess && shouldEnforceForThisDate;
  
  console.log('🔍 [MEETING CARD DEBUG] Final decision for', meeting.track.name, ':', {
    meetingDate: meeting.meeting_date,
    isAuthenticated,
    hasTrackAccess,
    shouldEnforceForThisDate,
    needsUpgrade,
    isPaywallTestUser,
    shouldEnforcePaywall
  });
  
  // Get track condition badge style
  const getTrackConditionBadgeClass = () => {
    if (!meeting.trackConditions || !shouldShowTrackCondition(meeting.trackConditions)) {
      return 'unknown';
    }

    const condition = meeting.trackConditions.trackCondition.toLowerCase();

    switch (condition) {
      case 'soft':
        return 'soft';
      case 'heavy':
        return 'heavy';
      case 'good':
      case 'firm':
      case 'fast':
        return 'good';
      case 'synthetic':
        return 'synthetic';
      default:
        return 'unknown';
    }
  };

  // Get ROI color class
  const getRoiColorClass = (value: number | null) => {
    if (value === null || value === undefined) return 'pending';
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'pending';
  };

  const LOW_STRIKE_RATE_THRESHOLD = 26;
  const isLowStrikeRate =
    meeting.track.performance?.top_1_win_percentage != null &&
    meeting.track.performance.top_1_win_percentage < LOW_STRIKE_RATE_THRESHOLD;

  return (
    <div className={`meeting-card-light ${className} ${needsUpgrade ? 'racing-card-upgrade' : ''} ${isAbandoned ? 'racing-card-abandoned' : ''} ${meeting.is_training ? 'training' : ''} ${meeting.is_private ? 'private' : ''}`}>
        {/* Card Header with Location and Track Condition */}
        <div className="card-header">
          <div>
            <div className="location">
              {meeting.is_private && (
                <svg className="w-4 h-4 inline mr-1 text-amber-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              )}
              {needsUpgrade && (
                <svg className="w-4 h-4 inline mr-1 text-amber-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              )}
              {meeting.is_training && (
                <svg className="w-4 h-4 inline mr-1 text-blue-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {meeting.track.name}
            </div>
            <div className="state">{meeting.track.state.toUpperCase()}</div>
            {isLowStrikeRate && (
              <div className="low-strike-rate-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Lower performing track - actively tuning
              </div>
            )}
          </div>

          {/* Track Condition Badge */}
          {shouldShowTrackCondition(meeting.trackConditions) && (
            <div className={`badge ${getTrackConditionBadgeClass()}`}>
              <span className="track-condition-icon">
                {getTrackConditionStyle(meeting.trackConditions!.trackCondition).icon}
              </span>
              <span>{formatTrackCondition(meeting.trackConditions!.trackCondition)}</span>
            </div>
          )}
        </div>

        {/* Stats Panel for Statistics */}
        {hasPerformanceData && (
          <div className="stats-panel">
              <div className="stat-item">
                <div className="meeting-stat-label">Top Pick</div>
                <div className="meeting-stat-value">{meetingStats.top1Winners}/{meetingStats.top1Picks}</div>
              </div>
              <div className="stat-item">
                <div className="meeting-stat-label">Total Winners</div>
                <div className="meeting-stat-value">{meetingStats.totalWinners}/{meetingStats.totalRaces}</div>
              </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        {hasPerformanceData && meeting.raceResultSegments && meeting.raceResultSegments.length > 0 && (
          <div className="meeting-expand-container">
            <button
              className="meeting-expand-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsIconsExpanded(!isIconsExpanded);
              }}
            >
              <span>{isIconsExpanded ? 'Hide Details' : 'Show Details'}</span>
              <span className={`meeting-expand-icon ${isIconsExpanded ? 'expanded' : ''}`}>▼</span>
            </button>
          </div>
        )}

        {/* Race Indicators */}
        {hasPerformanceData && meeting.raceResultSegments && meeting.raceResultSegments.length > 0 && (
          <div className="race-indicators-container">
            <div className="race-indicators">
              {meeting.raceResultSegments.map((segment) => (
                <div key={segment.race_number} className="race-column">
                  {/* Icons Stack - only visible when expanded */}
                  <div className={`race-icons-stack ${isIconsExpanded ? 'visible' : ''}`}>
                    {segment.top_pick_won && (
                      <div className="race-icon icon-star" title="Top Pick Won">★</div>
                    )}
                    {segment.value_bet_won && (
                      <div className="race-icon icon-value" title="Value Bet Won">V</div>
                    )}
                    {segment.quinella_hit && (
                      <div className="race-icon icon-quinella" title="Quinella">Q</div>
                    )}
                    {segment.trifecta_hit && (
                      <div className="race-icon icon-trifecta" title="Trifecta">T</div>
                    )}
                    {segment.first_four_hit && (
                      <div className="race-icon icon-first-four" title="First Four">F</div>
                    )}
                    {segment.quaddie_hit && (
                      <div className="race-icon icon-quaddie" title="Quaddie">Q4</div>
                    )}
                    {segment.early_quaddie_hit && (
                      <div className="race-icon icon-early-quaddie" title="Early Quaddie">EQ</div>
                    )}
                  </div>
                  {/* Race Bar */}
                  <div
                    className={`race-bar ${
                      segment.had_winner === null || segment.had_winner === undefined
                        ? 'pending'
                        : segment.had_winner
                        ? 'winner'
                        : 'loss'
                    }`}
                    title={`Race ${segment.race_number}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ROI Section */}
        {hasPerformanceData && (
          <div className={`roi-section ${
            (meetingStats.roi === null ||
             meetingStats.roi === undefined ||
             meeting.performanceImproved?.value_roi === null ||
             meeting.performanceImproved?.value_roi === undefined ||
             meeting.performanceImproved?.value_roi === -100) ? 'single-roi' : ''
          }`}>
            <div className="roi-item">
              <div className="meeting-roi-label">Top Pick ROI</div>
              {meetingStats.roi !== null ? (
                <div className={`meeting-roi-value ${getRoiColorClass(meetingStats.roi)}`}>
                  {meetingStats.roi.toFixed(1)}%
                </div>
              ) : (
                <div className="meeting-roi-value pending">
                  In Progress
                </div>
              )}
            </div>
            {meeting.performanceImproved?.value_roi !== null &&
             meeting.performanceImproved?.value_roi !== undefined &&
             meeting.performanceImproved?.value_roi !== -100 && (
              <div className="roi-item">
                <div className="meeting-roi-label">Value ROI</div>
                <div className={`meeting-roi-value ${getRoiColorClass(meeting.performanceImproved.value_roi)}`}>
                  {meeting.performanceImproved.value_roi.toFixed(1)}%
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="buttons">
          <>
            {isAuthenticated || isPreview ? (
                hasTrackAccess || isPreview || !shouldEnforceForThisDate ? (
                  <Link
                    to={formatTipsPageUrl(
                      meeting.track.state,
                      meeting.track.code,
                      formatDatePath(new Date(meeting.meeting_date)),
                      isPreview
                    )}
                    className="btn btn-tips"
                  >
                    TIPS
                  </Link>
                ) : (
                  <Link
                    to={formatTipsPageUrl(
                      meeting.track.state,
                      meeting.track.code,
                      formatDatePath(new Date(meeting.meeting_date)),
                      isPreview
                    )}
                    className="btn btn-upgrade"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    UPGRADE
                  </Link>
                )
              ) : (
                <Link
                  to="/login"
                  className="btn btn-tips"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  TIPS
                </Link>
              )}

              {hasPerformanceData && !needsUpgrade ? (
                isPreview ? (
                  <Link
                    to={`/preview/${formatDatePath(new Date(meeting.meeting_date))}/${meeting.track.code}/results`}
                    className="btn btn-results"
                  >
                    RESULTS
                  </Link>
                ) : (
                  <Link
                    to={`/${formatDatePath(new Date(meeting.meeting_date))}/results/${meeting.track.code}`}
                    className="btn btn-results"
                  >
                    RESULTS
                  </Link>
                )
              ) : (
                <button className="btn btn-results" disabled>RESULTS</button>
              )}
          </>
        </div>

      {/* Expanded performance section - keeping existing functionality */}
      {expanded && (
        <div className="meeting-performance-details">
          {hasPerformanceData ? (
            /* Show this meeting's performance data */
            <>
              <div className="performance-details-header">Meeting Performance</div>
              <div className="performance-detail">
                <span className="detail-label">Top 1 Winners:</span>
                <span className="detail-value">
                  {meetingStats.top1Winners}/{meetingStats.top1Picks} ({meetingStats.top1WinnersPercentage.toFixed(1)}%)
                </span>
              </div>
              <div className="performance-detail">
                <span className="detail-label">Total Winners:</span>
                <span className="detail-value">
                  {meetingStats.totalWinners}/{meetingStats.totalRaces} ({meetingStats.totalWinnersPercentage.toFixed(1)}%)
                </span>
              </div>
              <div className="performance-detail">
                <span className="detail-label">Top 1 ROI:</span>
                {meetingStats.roi !== null ? (
                  <span className={`detail-value ${meetingStats.roi >= 0 ? 'positive' : 'negative'}`}>
                    {meetingStats.roi.toFixed(1)}%
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-white">
                    <svg className="w-3 h-3 mr-1 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    In Progress
                  </span>
                )}
              </div>
              <div className="performance-detail">
                <span className="detail-label">Value ROI:</span>
                {meeting.performanceImproved && meeting.performanceImproved.value_roi !== null && meeting.performanceImproved.value_roi !== undefined ? (
                  <span className={`detail-value ${meeting.performanceImproved.value_roi >= 0 ? 'positive' : 'negative'}`}>
                    {meeting.performanceImproved.value_roi.toFixed(1)}%
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-white">
                    <svg className="w-3 h-3 mr-1 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    In Progress
                  </span>
                )}
              </div>
              {meeting.exoticBets && (
                <>
                  <div className="performance-detail">
                    <span className="detail-label">Quinella Success:</span>
                    <span className="detail-value">
                      {meeting.exoticBets.quinellas}/{meeting.performanceImproved?.total_races || 0}
                    </span>
                  </div>
                  <div className="performance-detail">
                    <span className="detail-label">Boxed Trifecta Success:</span>
                    <span className="detail-value">
                      {meeting.exoticBets.box_trifectas}/{meeting.performanceImproved?.total_races || 0}
                    </span>
                  </div>
                </>
              )}
            </>
          ) : (
            /* Show historical track performance */
            meeting.performance && (
              <>
                <div className="performance-details-header">Last Meeting Performance</div>
                <div className="performance-detail">
                  <span className="detail-label">Top 1 Winners:</span>
                  <span className="detail-value">
                    {meeting.performance.top_1_win_percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="performance-detail">
                  <span className="detail-label">Total Winners Found:</span>
                  <span className="detail-value">
                    {(meeting.performance.top_1_win_percentage +
                      meeting.performance.top_2_win_percentage +
                      meeting.performance.top_3_win_percentage).toFixed(1)}%
                  </span>
                </div>
                {meeting.exoticBets && (
                  <>
                    <div className="performance-detail">
                      <span className="detail-label">Quinella Success:</span>
                      <span className="detail-value">
                        {meeting.exoticBets.quinellas}/{meeting.race_count || 0}
                      </span>
                    </div>
                    <div className="performance-detail">
                      <span className="detail-label">Boxed Trifecta Success:</span>
                      <span className="detail-value">
                        {meeting.exoticBets.box_trifectas}/{meeting.race_count || 0}
                      </span>
                    </div>
                  </>
                )}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingCard;
